// Lightweight country detection used to decide GDPR opt-in vs CCPA opt-out.
// Uses the free https://api.country.is endpoint (no API key, no rate-limit issues
// at our volume) and caches the result for 30 days in localStorage.

const KEY = "cws-geo-country-v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

// EEA + UK + Switzerland (treat all as opt-in for safety).
const GDPR_COUNTRIES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
  "IS","LI","NO", // EEA
  "GB","CH",
]);

type Cached = { country: string; ts: number };

export function getCachedCountry(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Cached;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed.country || null;
  } catch {
    return null;
  }
}

export async function detectCountry(): Promise<string | null> {
  const cached = getCachedCountry();
  if (cached) return cached;
  try {
    const res = await fetch("https://api.country.is/", { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { country?: string };
    const country = (data.country || "").toUpperCase();
    if (country && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(KEY, JSON.stringify({ country, ts: Date.now() } satisfies Cached));
      } catch { /* noop */ }
    }
    return country || null;
  } catch {
    return null;
  }
}

export function isGdprCountry(country: string | null | undefined): boolean {
  if (!country) return false;
  return GDPR_COUNTRIES.has(country.toUpperCase());
}
